import React, { useState, useEffect } from "react";
import { FaCamera, FaPencilAlt } from "react-icons/fa";
import { AiFillEye, AiFillEyeInvisible } from "react-icons/ai";
import "../css/css_pages/Account.css";
import Cookies from "js-cookie";
import config from "../../config";
import PopupMessage from "../layouts/PopupMessage";
import { useTranslation } from 'react-i18next';

const Account = ({ setIsLoggedIn }) => {
  const { t } = useTranslation();
  const [isEditingPassword, setIsEditingPassword] = useState(false);
  const [passwordDetails, setPasswordDetails] = useState({
    oldPassword: "",
    newPassword: "",
    confirmPassword: "",
  });
  const [showPasswords, setShowPasswords] = useState({
    oldPassword: false,
    newPassword: false,
    confirmPassword: false,
  });
  const [userType, setUserType] = useState("");
  const [userProfile, setUserProfile] = useState({
    profilePicture: "",
    full_name: "",
    type: "",
    emp_no: "",
    phone_number: "",
  });

  const [clientProfile, setClientProfile] = useState({
    profilePicture: "",
    customer_name: "",
    type: "",
    address: "",
    customer_id: "",
    phone: "",
  });

  const [alertMessage, setAlertMessage] = useState(null);
  const fileInputRef = React.createRef();
  let DEFAULT_PROFILE_IMAGE = null;

  const [isEditingDashboard, setIsEditingDashboard] = useState(false);
  const [dashboardDetails, setDashboardDetails] = useState({
    phone_number: userProfile.phone_number,
  });

  useEffect(() => {
    const fetchUserProfile = async () => {
      try {
        const userTypeFromCookie = Cookies.get("userType");
        const token = Cookies.get("authToken");
        setUserType(userTypeFromCookie);

        if (!token) {
          throw new Error("Authentication token not found.");
        }

        const response = await fetch(`${config.API_URL}/user/profile`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        if (!response.ok) {
          throw new Error("Failed to fetch user profile");
        }

        const data = await response.json();

        let profilePictureUrl = DEFAULT_PROFILE_IMAGE;

        const profilePictureResponse = await fetch(
          `${config.API_URL}/user/profile-image`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );

        if (!profilePictureResponse.ok) {
          const profilePictureResponse = await fetch(
            `${config.API_URL}/user/static/utils/resources/profile.jpg`,
            {
              headers: {
                Authorization: `Bearer ${token}`,
              },
            }
          );
          const profilePictureBlob = await profilePictureResponse.blob();
          profilePictureUrl = URL.createObjectURL(profilePictureBlob);
        } else {
          const profilePictureBlob = await profilePictureResponse.blob();
          profilePictureUrl = URL.createObjectURL(profilePictureBlob);
        }

        if (["TSO Online", "Online TSO"].includes(userTypeFromCookie)) {
          setUserProfile({
            profilePicture: profilePictureUrl,
            full_name: data.full_name || "",
            type: data.type || "",
            emp_no: data.emp_no || "",
            phone_number: data.phone_number || "",
          });
          setDashboardDetails({
            phone_number: data.phone_number || "",
          });
        } else {
          setClientProfile({
            profilePicture: profilePictureUrl,
            customer_name: data.customer_name || "",
            type: data.type || "",
            address: data.address || "",
            customer_id: data.customer_id || "",
            phone: data.phone || "",
          });
        }
      } catch (error) {
        console.error("Error fetching user profile:", error);
        if (userType === "Client") {
          setClientProfile({
            profilePicture: DEFAULT_PROFILE_IMAGE,
            customer_name: "",
            type: "",
            address: "",
            customer_id: "",
            phone: "",
          });
        } else {
          setUserProfile({
            profilePicture: DEFAULT_PROFILE_IMAGE,
            full_name: "",
            type: "",
            emp_no: "",
            phone_number: "",
          });
        }
      }
    };

    fetchUserProfile();
  }, [userType]);

  const showPopupMessage = (message) => {
    setAlertMessage(message);
  };

  const closePopupMessage = () => {
    setAlertMessage(null);
  };

  const handleProfilePictureChange = async (event) => {
    const file = event.target.files[0];
    const token = Cookies.get("authToken");
    if (file) {
      const formData = new FormData();
      formData.append("image", file);

      try {
        const response = await fetch(`${config.API_URL}/user/update-profile`, {
          method: "PUT",
          headers: {
            Authorization: `Bearer ${token}`,
          },
          body: formData,
        });

        if (response.ok) {
          showPopupMessage(t('imageUploadSuccess'));
          setTimeout(() => {
            window.location.reload();
          }, 2000);
        } else {
          console.error("Failed to upload image:", response.statusText);
          showPopupMessage(t('updateFailed'));
        }
      } catch (error) {
        console.error("Error uploading image:", error);
        showPopupMessage(t('errorOccurred'));
      }
    }
  };

  const triggerFileInput = () => {
    fileInputRef.current.click();
  };

  const handlePasswordUpdate = async () => {
    const { oldPassword, newPassword, confirmPassword } = passwordDetails;

    if (newPassword !== confirmPassword) {
      showPopupMessage(t('passwordMismatch'));
      return;
    }

    try {
      const token = Cookies.get("authToken");
      const response = await fetch(`${config.API_URL}/user/reset-password`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          old_password: oldPassword,
          new_password: newPassword,
          confirm_password: newPassword,
        }),
      });

      if (response.ok) {
        showPopupMessage(t('passwordUpdateSuccess'));
        setIsEditingPassword(false);
        setPasswordDetails({
          oldPassword: "",
          newPassword: "",
          confirmPassword: "",
        });
      } else {
        console.error("Failed to update password:", response.statusText);
        showPopupMessage(t('updateFailed'));
      }
    } catch (error) {
      console.error("Error updating password:", error);
      showPopupMessage(t('errorOccurred'));
    }
  };

  const togglePasswordVisibility = (field) => {
    setShowPasswords((prev) => ({
      ...prev,
      [field]: !prev[field],
    }));
  };

  const handleDashboardUpdate = async () => {
    const token = Cookies.get("authToken");

    try {
      const response = await fetch(`${config.API_URL}/user/update-dashboard`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(dashboardDetails),
      });

      if (response.ok) {
        showPopupMessage(t('dashboardUpdateSuccess'));
        setIsEditingDashboard(false);
      } else {
        console.error("Failed to update dashboard:", response.statusText);
        showPopupMessage(t('updateFailed'));
      }
    } catch (error) {
      console.error("Error updating dashboard:", error);
      showPopupMessage(t('errorOccurred'));
    }
  };

  return (
    <div className="profile-container">
      {alertMessage && (
        <PopupMessage message={alertMessage} onClose={closePopupMessage} />
      )}

      <h1 className="profile-title">{t('accountTitle')}</h1>
      <div className="profile-card">
        <div className="profile-picture">
          <img
            src={
              ["TSO Online", "Online TSO"].includes(userType)
                ? userProfile.profilePicture || DEFAULT_PROFILE_IMAGE
                : clientProfile.profilePicture || DEFAULT_PROFILE_IMAGE
            }
            alt="Profile"
            onError={(e) => (e.target.src = DEFAULT_PROFILE_IMAGE)}
          />
          <div className="profile-picture-edit" onClick={triggerFileInput}>
            <FaCamera className="camera-icon" />
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handleProfilePictureChange}
              className="profile-picture-input"
            />
          </div>
        </div>
        <div className="profile-details">
          {["TSO Online", "Online TSO"].includes(userType) ? (
            <>
              <p>
                <strong>{t('name')}:</strong> {userProfile.full_name}
              </p>
              <p>
                <strong>{t('type')}:</strong> {userProfile.type}
              </p>
              <p>
                <strong>{t('employeeNo')}:</strong> {userProfile.emp_no}
              </p>
              <p>
                <strong>{t('phoneNumber')}:</strong> {userProfile.phone_number}
              </p>
              <button
                className="update-password-button"
                onClick={() => setIsEditingPassword(true)}
              >
                <FaPencilAlt className="pencil-icon" /> {t('updatePassword')}
              </button>
            </>
          ) : (
            <>
              <p>
                <strong>{t('name')}:</strong> {clientProfile.customer_name}
              </p>
              <p>
                <strong>{t('type')}:</strong> {clientProfile.type}
              </p>
              <p>
                <strong>{t('address')}:</strong> {clientProfile.address}
              </p>
              <p>
                <strong>{t('customerId')}:</strong> {clientProfile.customer_id}
              </p>
              <p>
                <strong>{t('phone')}:</strong> {clientProfile.phone}
              </p>
              <button
                className="update-password-button"
                onClick={() => setIsEditingPassword(true)}
              >
                <FaPencilAlt className="pencil-icon" /> {t('updatePassword')}
              </button>
            </>
          )}
        </div>
      </div>

      {isEditingPassword && (
        <>
          <div
            className="overlay"
            onClick={() => setIsEditingPassword(false)}
          />
          <div className="password-popup">
            <h2>{t('editPassword')}</h2>
            <div className="password-field">
              <label>{t('oldPassword')}:</label>
              <input
                type={showPasswords.oldPassword ? "text" : "password"}
                value={passwordDetails.oldPassword}
                onChange={(e) =>
                  setPasswordDetails({
                    ...passwordDetails,
                    oldPassword: e.target.value,
                  })
                }
                autoComplete="new-password"
              />
              <span
                className="toggle-password"
                onClick={() => togglePasswordVisibility("oldPassword")}
              >
                {showPasswords.oldPassword ? (
                  <AiFillEyeInvisible />
                ) : (
                  <AiFillEye />
                )}
              </span>
            </div>

            <div className="password-field">
              <label>{t('newPassword')}:</label>
              <input
                type={showPasswords.newPassword ? "text" : "password"}
                value={passwordDetails.newPassword}
                onChange={(e) =>
                  setPasswordDetails({
                    ...passwordDetails,
                    newPassword: e.target.value,
                  })
                }
              />
              <span
                className="toggle-password"
                onClick={() => togglePasswordVisibility("newPassword")}
              >
                {showPasswords.newPassword ? (
                  <AiFillEyeInvisible />
                ) : (
                  <AiFillEye />
                )}
              </span>
            </div>

            <div className="password-field">
              <label>{t('confirmPassword')}:</label>
              <input
                type={showPasswords.confirmPassword ? "text" : "password"}
                value={passwordDetails.confirmPassword}
                onChange={(e) =>
                  setPasswordDetails({
                    ...passwordDetails,
                    confirmPassword: e.target.value,
                  })
                }
              />
              <span
                className="toggle-password"
                onClick={() => togglePasswordVisibility("confirmPassword")}
              >
                {showPasswords.confirmPassword ? (
                  <AiFillEyeInvisible />
                ) : (
                  <AiFillEye />
                )}
              </span>
            </div>

            <button onClick={handlePasswordUpdate}>{t('update')}</button>
            <button onClick={() => setIsEditingPassword(false)}>{t('cancel')}</button>
          </div>
        </>
      )}

      {isEditingDashboard && (
        <>
          <div
            className="overlay"
            onClick={() => setIsEditingDashboard(false)}
          />
          <div className="dashboard-popup">
            <h2>{t('editDashboard')}</h2>
            <div className="dashboard-field">
              <label>{t('phoneNumber')}:</label>
              <input
                type="text"
                value={dashboardDetails.phone_number}
                onChange={(e) =>
                  setDashboardDetails({
                    ...dashboardDetails,
                    phone_number: e.target.value,
                  })
                }
              />
            </div>
            <button onClick={handleDashboardUpdate}>{t('update')}</button>
            <button onClick={() => setIsEditingDashboard(false)}>{t('cancel')}</button>
          </div>
        </>
      )}
    </div>
  );
};

export default Account;